"use client";

import {
  Container,
  Title,
  Text,
  Box,
  Stack,
  SimpleGrid,
  Group,
  Button,
  Anchor,
  Divider,
  Paper,
  ThemeIcon,
} from "@mantine/core";
import { IconArrowRight, IconCheck, IconX } from "@tabler/icons-react";
import Link from "next/link";
import { trackCtaClick } from "@/lib/analytics";

const topHairColors = [
  { color: "#8B4513", name: "Auburn", why: "The classic green-eye enhancer—red-green contrast makes eyes pop." },
  { color: "#B87333", name: "Copper", why: "Warm metallic tones bring out golden flecks in green eyes." },
  { color: "#A0522D", name: "Chestnut Brown", why: "Rich warmth without being too bold—universally flattering." },
  { color: "#D4A76A", name: "Honey Blonde", why: "Soft, warm blonde that complements green eyes beautifully." },
  { color: "#722F37", name: "Burgundy", why: "Deep red undertones create stunning contrast with green." },
  { color: "#8B0000", name: "Deep Red", why: "Dramatic and eye-catching—maximizes the red-green effect." },
  { color: "#3D2314", name: "Dark Chocolate", why: "Rich depth that makes green eyes stand out." },
  { color: "#C19A6B", name: "Caramel", why: "Warm, sun-kissed tones that enhance green eye color." },
  { color: "#CD853F", name: "Ginger", why: "Natural-looking red that's striking with green eyes." },
  { color: "#DAA520", name: "Golden Blonde", why: "Bright and warm—perfect for fair skin with green eyes." },
  { color: "#5C4033", name: "Espresso", why: "High contrast for dramatic, intense green eyes." },
  { color: "#D2691E", name: "Cinnamon", why: "Warm spice tones that make green eyes glow." },
];

const warmSkinHairColors = [
  { color: "#B87333", name: "Copper" },
  { color: "#D4A76A", name: "Honey Blonde" },
  { color: "#C19A6B", name: "Caramel" },
  { color: "#CD853F", name: "Ginger" },
  { color: "#A0522D", name: "Warm Chestnut" },
];

const coolSkinHairColors = [
  { color: "#722F37", name: "Burgundy" },
  { color: "#3D2314", name: "Cool Espresso" },
  { color: "#8B4513", name: "Cool Auburn" },
  { color: "#4A3728", name: "Ash Brown" },
  { color: "#1C1C1C", name: "Soft Black" },
];

const neutralSkinHairColors = [
  { color: "#8B4513", name: "Auburn" },
  { color: "#A0522D", name: "Chestnut" },
  { color: "#5C4033", name: "Rich Brown" },
  { color: "#B87333", name: "Copper" },
  { color: "#722F37", name: "Burgundy" },
];

const fairSkinHairColors = [
  { color: "#D4A76A", name: "Honey Blonde" },
  { color: "#DEB887", name: "Strawberry Blonde" },
  { color: "#B87333", name: "Light Copper" },
  { color: "#8B4513", name: "Auburn" },
  { color: "#CD853F", name: "Light Ginger" },
];

const colorsToAvoid = [
  { color: "#C0C0C0", name: "Platinum/Silver", why: "Can wash out green eyes and look harsh", instead: "Honey or golden blonde" },
  { color: "#808080", name: "Ashy Gray Tones", why: "Dulls the vibrancy of green eyes", instead: "Warm browns or copper" },
  { color: "#228B22", name: "Green-Toned Hair", why: "Matches your eyes and makes both look dull", instead: "Complementary reds and coppers" },
  { color: "#FFFF00", name: "Brassy Yellow", why: "Clashes with green and looks unnatural", instead: "Golden or honey tones" },
  { color: "#FFA500", name: "Orange (unflattering)", why: "Too close to green on the color wheel", instead: "Copper or auburn" },
];

function ColorSwatch({ color, size = 48 }: { color: string; size?: number }) {
  return (
    <Box
      style={{
        width: size,
        height: size,
        backgroundColor: color,
        borderRadius: 8,
        border: color === "#FFFFFF" || color === "#FAF9F6" ? "1px solid #e0e0e0" : "none",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      }}
    />
  );
}

export default function BestHairColorForGreenEyesContent() {
  return (
    <Container size="md" py="xl">
      {/* HERO */}
      <Stack gap="xs" mb={48}>
        <Title order={1} size={36} lh={1.2}>
          Best Hair Color for Green Eyes
        </Title>
        <Text size="xl" c="dimmed" maw={600}>
          Find the perfect hair color to make your green eyes stand out—based on skin tone, undertone, and color theory.
        </Text>
      </Stack>

      {/* SECTION 1: TOP 12 HAIR COLORS */}
      <Box mb={64}>
        <Title order={2} size={28} mb={32}>
          Top 12 Hair Colors That Make Green Eyes Pop
        </Title>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={24}>
          {topHairColors.map((item) => (
            <Group key={item.name} gap="md" align="flex-start">
              <ColorSwatch color={item.color} size={56} />
              <Box style={{ flex: 1 }}>
                <Text fw={600} size="lg">{item.name}</Text>
                <Text c="dimmed" size="sm">{item.why}</Text>
              </Box>
            </Group>
          ))}
        </SimpleGrid>

        {/* Subtle CTA */}
        <Paper withBorder p="lg" mt={40} radius="md" bg="gray.0">
          <Text size="md" mb="sm">
            Not sure which shade is <em>your</em> best match?
          </Text>
          <Button
            component={Link}
            href="/ai-color-analysis"
            color="teal"
            rightSection={<IconArrowRight size={16} />}
          >
            Get your personalized color palette
          </Button>
        </Paper>

        {/* Hair color changer CTA */}
        <Paper withBorder p="lg" mt={24} radius="md" bg="grape.0">
          <Text size="md" mb="sm">
            Stop guessing—see these colors on <em>your</em> hair.
          </Text>
          <Button
            component={Link}
            href="/ai-hair-color-try-on"
            color="grape"
            variant="light"
            rightSection={<IconArrowRight size={16} />}
            onClick={() => trackCtaClick("hair_seo_green_eyes")}
          >
            Try any shade free with our AI hair color changer
          </Button>
        </Paper>
      </Box>

      <Divider my={48} />

      {/* SECTION 2: THE SCIENCE */}
      <Box mb={64}>
        <Title order={2} size={28} mb={16}>
          Why These Colors Work: The Color Theory
        </Title>

        <Text c="dimmed" mb={32} maw={700}>
          Green and red are complementary colors on the color wheel. This means red-based hair colors (copper, auburn, burgundy) create the strongest visual contrast with green eyes, making them appear more vibrant and intense.
        </Text>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={24} mb={32}>
          <Paper withBorder p="lg" radius="md">
            <Group gap="xs" mb="md">
              <ThemeIcon color="green" size="sm" radius="xl">
                <IconCheck size={14} />
              </ThemeIcon>
              <Text fw={600}>Best Contrast</Text>
            </Group>
            <Group gap="sm" mb="md">
              <ColorSwatch color="#8B4513" size={40} />
              <ColorSwatch color="#B87333" size={40} />
              <ColorSwatch color="#722F37" size={40} />
              <ColorSwatch color="#8B0000" size={40} />
            </Group>
            <Text size="sm" c="dimmed">
              Red-based tones create complementary contrast that makes green eyes vivid.
            </Text>
          </Paper>

          <Paper withBorder p="lg" radius="md">
            <Group gap="xs" mb="md">
              <ThemeIcon color="red" size="sm" radius="xl">
                <IconX size={14} />
              </ThemeIcon>
              <Text fw={600}>Low Contrast</Text>
            </Group>
            <Group gap="sm" mb="md">
              <ColorSwatch color="#C0C0C0" size={40} />
              <ColorSwatch color="#808080" size={40} />
              <ColorSwatch color="#228B22" size={40} />
              <ColorSwatch color="#FFFF00" size={40} />
            </Group>
            <Text size="sm" c="dimmed">
              Ashy, silver, or green-toned hair dulls the vibrancy of green eyes.
            </Text>
          </Paper>
        </SimpleGrid>

        <Paper p="lg" radius="md" bg="blue.0">
          <Text fw={600} mb="xs">The takeaway:</Text>
          <Text size="sm">
            Even subtle red or copper undertones in your hair can enhance green eyes. You don't need to go full red—warm browns with hints of auburn work beautifully too.
          </Text>
        </Paper>
      </Box>

      <Divider my={48} />

      {/* SECTION 3: BY SKIN TONE */}
      <Box mb={64}>
        <Title order={2} size={28} mb={32}>
          Best Hair Colors for Green Eyes by Skin Tone
        </Title>

        <Stack gap={40}>
          {/* Fair Skin */}
          <Box>
            <Title order={3} size={22} mb={16}>
              Fair Skin + Green Eyes
            </Title>
            <Text size="sm" c="dimmed" mb={16}>Light, porcelain, or pale skin tones.</Text>

            <Group gap="lg" mb={16}>
              {fairSkinHairColors.map((item) => (
                <Stack key={item.name} gap={4} align="center">
                  <ColorSwatch color={item.color} size={48} />
                  <Text size="xs" ta="center">{item.name}</Text>
                </Stack>
              ))}
            </Group>

            <Text size="sm" c="dimmed">
              <strong>Why they work:</strong> Light warm tones complement fair skin without overwhelming it, while making green eyes the focal point.
            </Text>
          </Box>

          {/* Warm Undertones */}
          <Box>
            <Title order={3} size={22} mb={16}>
              Warm Undertones + Green Eyes
            </Title>
            <Text size="sm" c="dimmed" mb={16}>Peach, golden, or olive undertones. Gold jewelry looks best.</Text>

            <Group gap="lg" mb={16}>
              {warmSkinHairColors.map((item) => (
                <Stack key={item.name} gap={4} align="center">
                  <ColorSwatch color={item.color} size={48} />
                  <Text size="xs" ta="center">{item.name}</Text>
                </Stack>
              ))}
            </Group>

            <Text size="sm" c="dimmed">
              <strong>Why they work:</strong> Warm hair tones complement your skin's natural warmth while enhancing the green in your eyes.
            </Text>
          </Box>

          {/* Cool Undertones */}
          <Box>
            <Title order={3} size={22} mb={16}>
              Cool Undertones + Green Eyes
            </Title>
            <Text size="sm" c="dimmed" mb={16}>Pink, red, or bluish undertones. Silver jewelry looks best.</Text>

            <Group gap="lg" mb={16}>
              {coolSkinHairColors.map((item) => (
                <Stack key={item.name} gap={4} align="center">
                  <ColorSwatch color={item.color} size={48} />
                  <Text size="xs" ta="center">{item.name}</Text>
                </Stack>
              ))}
            </Group>

            <Text size="sm" c="dimmed">
              <strong>Why they work:</strong> Cooler reds like burgundy and deeper browns add drama while staying harmonious with cool skin.
            </Text>
          </Box>

          {/* Neutral */}
          <Box>
            <Title order={3} size={22} mb={16}>
              Neutral Undertones + Green Eyes
            </Title>
            <Text size="sm" c="dimmed" mb={16}>Mix of both warm and cool. Both gold and silver jewelry work.</Text>

            <Group gap="lg" mb={16}>
              {neutralSkinHairColors.map((item) => (
                <Stack key={item.name} gap={4} align="center">
                  <ColorSwatch color={item.color} size={48} />
                  <Text size="xs" ta="center">{item.name}</Text>
                </Stack>
              ))}
            </Group>

            <Text size="sm" c="dimmed">
              <strong>Why they work:</strong> Neutral undertones have flexibility—you can go warmer or cooler depending on your preference.
            </Text>
          </Box>
        </Stack>

        {/* Stronger CTA */}
        <Paper withBorder p="lg" mt={40} radius="md" bg="teal.0">
          <Text fw={600} mb="sm">
            Most people guess their undertone wrong.
          </Text>
          <Button
            component={Link}
            href="/ai-color-analysis"
            color="teal"
            size="md"
            rightSection={<IconArrowRight size={16} />}
          >
            Find your exact undertone + best colors instantly
          </Button>
        </Paper>
      </Box>

      <Divider my={48} />

      {/* SECTION 4: COLORS TO AVOID */}
      <Box mb={64}>
        <Title order={2} size={28} mb={16}>
          Hair Colors That Don't Flatter Green Eyes
        </Title>

        <Text c="dimmed" mb={32}>
          Some hair colors can dull or clash with green eyes.
        </Text>

        <Stack gap={20}>
          {colorsToAvoid.map((item) => (
            <Group key={item.name} gap="md" align="flex-start">
              <ColorSwatch color={item.color} size={48} />
              <Box style={{ flex: 1 }}>
                <Group gap="xs">
                  <Text fw={600}>{item.name}</Text>
                  <Text c="red.6" size="sm">— {item.why}</Text>
                </Group>
                <Text size="sm" c="teal.7">
                  Instead, choose: <strong>{item.instead}</strong>
                </Text>
              </Box>
            </Group>
          ))}
        </Stack>
      </Box>

      <Divider my={48} />

      {/* SECTION 5: SPECIAL SECTION - GREEN EYE VARIATIONS */}
      <Box mb={64}>
        <Title order={2} size={28} mb={16}>
          Hair Colors for Different Shades of Green Eyes
        </Title>

        <Text c="dimmed" mb={24}>
          Not all green eyes are the same. Here's how to enhance your specific shade.
        </Text>

        <Stack gap={24}>
          <Paper withBorder p="lg" radius="md">
            <Text fw={600} mb={8}>Bright/Emerald Green Eyes</Text>
            <Text size="sm" c="dimmed">
              Your eyes are already vivid! Deep reds, burgundy, and rich auburn create stunning contrast. You can go bold with copper or deep chocolate brown.
            </Text>
          </Paper>

          <Paper withBorder p="lg" radius="md">
            <Text fw={600} mb={8}>Hazel-Green Eyes</Text>
            <Text size="sm" c="dimmed">
              You have golden flecks mixed with green. Warm caramel, honey blonde, and copper bring out the gold and make the green more prominent. Avoid cool ash tones.
            </Text>
          </Paper>

          <Paper withBorder p="lg" radius="md">
            <Text fw={600} mb={8}>Gray-Green Eyes</Text>
            <Text size="sm" c="dimmed">
              Your eyes have a cool, muted quality. Cool-toned browns, burgundy, and even soft black work beautifully. Stay away from overly brassy or orange tones.
            </Text>
          </Paper>

          <Paper withBorder p="lg" radius="md">
            <Text fw={600} mb={8}>Blue-Green/Teal Eyes</Text>
            <Text size="sm" c="dimmed">
              You can play up either the blue or green! Rich copper and auburn emphasize the green, while cool espresso and dark chocolate bring out the blue.
            </Text>
          </Paper>
        </Stack>
      </Box>

      <Divider my={48} />

      {/* SECTION 6: SEASONAL COLOR ANALYSIS */}
      <Box mb={64}>
        <Title order={2} size={28} mb={16}>
          The Secret: Your Color Season Matters
        </Title>

        <Text c="dimmed" mb={8}>
          While general advice helps, the truth is:
        </Text>

        <Paper p="md" radius="md" bg="gray.1" mb={24}>
          <Text fw={600} size="lg" ta="center">
            Not all green-eyed people are the same.
          </Text>
        </Paper>

        <Text mb={32}>
          Two people with green eyes can look completely different in the same hair color. That's because your <strong>color season</strong> determines your exact best palette—including hair, makeup, and wardrobe.
        </Text>

        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing={16} mb={32}>
          <Anchor component={Link} href="/color-palette/autumn/true" underline="never">
            <Paper withBorder p="md" radius="md" ta="center" style={{ cursor: "pointer" }}>
              <Group gap={4} justify="center" mb="xs">
                <Box w={16} h={16} bg="#8B4513" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#B87333" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#CD853F" style={{ borderRadius: 4 }} />
              </Group>
              <Text fw={600} size="sm">Autumn</Text>
              <Text size="xs" c="dimmed">Warm coppers & auburns</Text>
            </Paper>
          </Anchor>

          <Anchor component={Link} href="/color-palette/spring/true" underline="never">
            <Paper withBorder p="md" radius="md" ta="center" style={{ cursor: "pointer" }}>
              <Group gap={4} justify="center" mb="xs">
                <Box w={16} h={16} bg="#D4A76A" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#DAA520" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#DEB887" style={{ borderRadius: 4 }} />
              </Group>
              <Text fw={600} size="sm">Spring</Text>
              <Text size="xs" c="dimmed">Golden & honey tones</Text>
            </Paper>
          </Anchor>

          <Anchor component={Link} href="/color-palette/winter/true" underline="never">
            <Paper withBorder p="md" radius="md" ta="center" style={{ cursor: "pointer" }}>
              <Group gap={4} justify="center" mb="xs">
                <Box w={16} h={16} bg="#722F37" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#1C1C1C" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#3D2314" style={{ borderRadius: 4 }} />
              </Group>
              <Text fw={600} size="sm">Winter</Text>
              <Text size="xs" c="dimmed">Bold & high-contrast</Text>
            </Paper>
          </Anchor>

          <Anchor component={Link} href="/color-palette/summer/true" underline="never">
            <Paper withBorder p="md" radius="md" ta="center" style={{ cursor: "pointer" }}>
              <Group gap={4} justify="center" mb="xs">
                <Box w={16} h={16} bg="#4A3728" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#6B4423" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#8B7355" style={{ borderRadius: 4 }} />
              </Group>
              <Text fw={600} size="sm">Summer</Text>
              <Text size="xs" c="dimmed">Soft ash & muted tones</Text>
            </Paper>
          </Anchor>
        </SimpleGrid>

        {/* Primary CTA */}
        <Paper p="xl" radius="md" bg="teal.6" c="white" ta="center">
          <Text size="lg" fw={600} mb="sm">
            Get your personalized color palette
          </Text>
          <Text size="sm" mb="md" opacity={0.9}>
            Takes 30 seconds. See your exact best hair colors, makeup, and wardrobe colors.
          </Text>
          <Button
            component={Link}
            href="/ai-color-analysis"
            color="teal"
            variant="white"
            size="lg"
            rightSection={<IconArrowRight size={18} />}
          >
            Start Free Analysis
          </Button>
        </Paper>
      </Box>

      <Divider my={48} />

      {/* Plain text link to hair color changer */}
      <Text size="sm" c="dimmed" mb={48}>
        Curious how a shade actually looks on you? <Anchor component={Link} href="/ai-hair-color-try-on">Try our free AI hair color changer</Anchor>.
      </Text>

      {/* SECTION 7: FAQ */}
      <Box mb={48}>
        <Title order={2} size={28} mb={32}>
          Frequently Asked Questions
        </Title>

        <Stack gap={24}>
          <Box>
            <Text fw={600} mb={4}>What hair color makes green eyes pop the most?</Text>
            <Text c="dimmed">
              Red-based colors like auburn, copper, and burgundy create the strongest contrast with green eyes due to being complementary colors.
            </Text>
          </Box>

          <Box>
            <Text fw={600} mb={4}>Can I go blonde with green eyes?</Text>
            <Text c="dimmed">
              Absolutely! Warm blondes like honey, golden, and strawberry blonde are especially flattering. Avoid platinum or ashy blondes that may wash you out.
            </Text>
          </Box>

          <Box>
            <Text fw={600} mb={4}>Is brunette or redhead better for green eyes?</Text>
            <Text c="dimmed">
              Both work beautifully! Warm browns with red undertones give you the best of both worlds. Pure red maximizes the green-eye effect, while brown is more versatile.
            </Text>
          </Box>

          <Box>
            <Text fw={600} mb={4}>What about highlights for green eyes?</Text>
            <Text c="dimmed">
              Copper, caramel, or auburn highlights can enhance green eyes even in darker hair. They add warmth and dimension without a full color change.
            </Text>
          </Box>

          <Box>
            <Text fw={600} mb={4}>Will my natural hair color work with my green eyes?</Text>
            <Text c="dimmed">
              Usually yes! Your natural hair color evolved with your eye color. But if you want to enhance your eyes, adding warm or red tones almost always helps.
            </Text>
          </Box>
        </Stack>
      </Box>
    </Container>
  );
}
